import { pubText } from "../i18n/publicTranslation";
import React, { useEffect, useId, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import { DEFAULT_MODEL_PRICING, loadModelPricing, ModelPriceSort } from "../api/modelPricing";
import { useFeatureGates } from "../hooks/useFeatureGates";
import { useAppSelector } from "../store/hooks";
import ModelPricingSection from "./ModelPricingSection";
import ModelSortMenu from "./common/ModelSortMenu";
import FableTokenAmount from "./common/FableTokenAmount";
import UsageProductSelect from "./common/UsageProductSelect";
import { APP_BORDER, APP_SURFACE, APP_TEXT_SECONDARY, getAudioModeTokenCost, getImageQualityTokenCost, getStoryLengthMultiplier, getV2StoryTokenCost, getVideoModeTokenCost, imageQualityOptions, storyModeOptions, videoModeOptions } from "./createShared";
import { imageQualityLabels, StudioImageQuality } from "./studio/studioImageQuality";
import { FORM_FIELD_RHYTHM } from "./common/StoryFormControls";
import { productCardTitleSx } from "./common/productTypography";

function PricingSection({ title, id, action, children }: React.PropsWithChildren<{ title: string; id?: string; action?: React.ReactNode }>) {
  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: "calc(var(--fable-dashboard-modal-top, 72px) + 24px)", p: { xs: 2, sm: 3 }, minWidth: 0, bgcolor: APP_SURFACE, border: `1px solid ${APP_BORDER}`, borderRadius: "16px" }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: FORM_FIELD_RHYTHM.mini }}>
        <Typography component="h2" sx={{ ...productCardTitleSx, flex: 1, minWidth: 0 }}>{pubText(title)}</Typography>
        {pubText(action)}
      </Box>
      <Stack spacing={1.5} sx={{ color: APP_TEXT_SECONDARY, fontSize: 14, "& p": { fontSize: "inherit", lineHeight: 1.6 } }}>{pubText(children)}</Stack>
    </Box>
  );
}

function PriceTable({ label, columns, rows }: { label: string; columns: string[]; rows: { label: string; values: React.ReactNode[] }[] }) {
  return (
    <Box component="table" aria-label={pubText(label)} sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: { xs: 13, sm: 15 },
      "& th, & td": { py: 1.5, px: { xs: 0.5, sm: 1 }, borderBottom: `1px solid ${APP_BORDER}`, textAlign: "end", verticalAlign: "middle", color: "white", fontVariantNumeric: "tabular-nums" },
      "& tr > :first-child": { textAlign: "start", paddingInlineStart: 0 }, "& tr > :last-child": { paddingInlineEnd: 0 }, "& th": { fontWeight: 700, whiteSpace: "normal", overflowWrap: "anywhere" }, "& td": { whiteSpace: "nowrap" },
      "& thead th": { borderTop: `1px solid ${APP_BORDER}` } }}>
      <colgroup>{pubText(
        columns.map((column, i) => <col key={column} style={{ width: i === 0 ? "50%" : `${50 / (columns.length - 1)}%` }} />)
      )}</colgroup>
      <thead><tr>{pubText(columns.map(column => <th key={column} scope="col">{pubText(column)}</th>))}</tr></thead>
      <tbody>{pubText(
        rows.map(row => <tr key={row.label}><th scope="row">{pubText(row.label)}</th>{pubText(row.values.map((value, i) => <td key={i}>{pubText(
          typeof value === "number" || (typeof value === "string" && /^[+-]?\d+(?:\.\d+)?$/.test(value)) ? <FableTokenAmount amount={value} kind="price" /> : value
        )}</td>))}</tr>)
      )}</tbody>
    </Box>
  );
}

function usePricingCatalog() {
  const [catalog, setCatalog] = useState(DEFAULT_MODEL_PRICING);
  useEffect(() => {
    const controller = new AbortController();
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      loadModelPricing(controller.signal).then(value => { if (!controller.signal.aborted) setCatalog(value); }).catch(() => {});
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);
  return catalog;
}
const price = (value: number | undefined) => Number.isFinite(value) ? value! : "Unavailable";

function GambitPricing() {
  const catalog = usePricingCatalog();
  const [sort, setSort] = useState<ModelPriceSort>('overall');
  return (
    <Stack spacing={3}>
      <PricingSection title={pubText("Chat cost in Fable tokens per 500 words (estimated)")} id="model-pricing" action={<ModelSortMenu compact value={sort} onChange={setSort} />}><ModelPricingSection sort={sort} /></PricingSection>
      <PricingSection title={pubText("Images")} id="image-pricing">
        <Typography>{pubText(
          "Images are priced per image, based on quality. Each new variation is charged separately."
        )}</Typography>
        <PriceTable label={pubText("Gambit image token prices")} columns={["Image quality", "Per image"]} rows={(Object.keys(imageQualityLabels) as StudioImageQuality[]).map(quality => ({ label: imageQualityLabels[quality], values: [price(catalog?.imagePricing?.[quality])] }))} />
      </PricingSection>
      <PricingSection title={pubText("Audio")} id="audio-pricing">
        <Typography>{pubText(
          "Audio is priced per generation, based on type and length. Selected add-ons are charged on top."
        )}</Typography>
        <PriceTable label={pubText("Gambit audio token prices")} columns={["Audio type", "1–2 min", "2–4 min"]} rows={[
          ...storyModeOptions.map(mode => ({ label: mode.label, values: [price(catalog?.audioPricing?.Normal?.[mode.id]), price(catalog?.audioPricing?.Long?.[mode.id])] })),
          ...([ ["immersive", "Immersive add-on"], ["cinematic", "Cinematic add-on"] ] as const).map(([key, label]) => ({ label, values: [price(catalog?.audioPricing?.Normal?.[key]), price(catalog?.audioPricing?.Long?.[key])] })),
        ]} />
      </PricingSection>
      <PricingSection title={pubText("Video")} id="video-pricing"><Typography>{pubText("Video generation is priced per second.")}</Typography>
        <PriceTable label={pubText("Gambit video token prices")} columns={["Video quality", "Per second"]}
          rows={videoModeOptions.filter(mode => mode.id !== "none").map(mode => ({ label: mode.label, values: [price(catalog?.videoPricing?.[mode.id]?.tokensPerSecond)] }))} /></PricingSection>
    </Stack>
  );
}

function ClassicPricing() {
  const userId = useAppSelector(state => state.app.userId);
  const { data } = useFeatureGates(userId || "", Boolean(userId));
  const catalog = usePricingCatalog();
  const costs = catalog?.tokenCosts ?? data?.tokenCosts;
  const lengths = ["Normal", "Long"] as const;
  return (
    <Stack spacing={3}>
      <PricingSection title={pubText("Story prices")} id="classic-pricing">
        <Typography>{pubText("Per story or chapter. Includes writing, standard images and audio.")}</Typography>
        <PriceTable label={pubText("Go story token prices")} columns={["Story format", "1–2 min", "2–4 min"]} rows={storyModeOptions.map(mode => ({ label: mode.label, values: lengths.map(storyLength => getV2StoryTokenCost({ tokenCosts: costs, storyMode: mode.id, storyLength, imageQuality: "standard", videoMode: "none", includeBackgroundMusic: false, audioMode: "classic" })) }))} />
      </PricingSection>
      <PricingSection title={pubText("Audio upgrades")}>
        <Typography>{pubText(
          "Immersive adds environmental sound. Cinematic adds sound and an instrumental score. Choose one upgrade for Narrated or Dialogue; neither applies to Musicals."
        )}</Typography>
        <PriceTable label={pubText("Go audio upgrade token prices")} columns={["Audio upgrade", "1–2 min", "2–4 min"]} rows={([ ["immersive", "Immersive"], ["cinematic", "Cinematic"] ] as const).map(([mode, label]) => ({ label, values: lengths.map(length => getAudioModeTokenCost(costs, mode, length)) }))} />
      </PricingSection>
      <PricingSection title={pubText("Story quality upgrades")}>
        <Typography>{pubText(
          "Standard is included. Higher quality adds these tokens to your story price."
        )}</Typography>
        <PriceTable label={pubText("Go story quality upgrade token prices")} columns={["Story format & quality", "1–2 min", "2–4 min"]} rows={storyModeOptions.flatMap(mode =>
          imageQualityOptions.filter(quality => quality.id !== "standard").map(quality => ({
            label: `${mode.label} · ${quality.label}`,
            values: lengths.map(length => getImageQualityTokenCost(costs, mode.id, length, quality.id)),
          })),
        )} />
      </PricingSection>
      <PricingSection title={pubText("Video upgrades")}>
        <PriceTable label={pubText("Go video upgrade token prices")} columns={["Video quality", "1–2 min", "2–4 min"]} rows={videoModeOptions.filter(mode => mode.id !== "none").map(mode => ({ label: mode.label, values: lengths.map(length => getVideoModeTokenCost(costs, mode.id) * getStoryLengthMultiplier(length)) }))} />
      </PricingSection>
    </Stack>
  );
}

export function CreationPricingToggle({ id }: { id: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const product = searchParams.get("pricing") === "classic" ? "classic" : "gambit";
  const setProduct = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("pricing", value);
    setSearchParams(next, { replace: true });
  };
  return <Box sx={{ marginInlineStart: "auto" }}><UsageProductSelect label={pubText("Creation pricing")} value={product} onChange={setProduct} /></Box>;
}

export default function CreationPricingSection({ id: suppliedId, showToggle = true }: { id?: string; showToggle?: boolean }) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();
  const product = searchParams.get("pricing") === "classic" ? "classic" : "gambit";
  useEffect(() => {
    if (!["#model-pricing", "#image-pricing", "#audio-pricing", "#video-pricing", "#classic-pricing"].includes(hash)) return;
    const frame = requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }));
    return () => cancelAnimationFrame(frame);
  }, [hash, product]);
  return (
    <Stack gap={2.5}>
      {pubText(showToggle && <CreationPricingToggle id={id} />)}
      <Box role="region" id={`${id}-gambit-panel`} aria-label={pubText("Gambit pricing")} hidden={product !== "gambit"}>{pubText(product === "gambit" && <GambitPricing />)}</Box>
      <Box role="region" id={`${id}-classic-panel`} aria-label={pubText("Go pricing")} hidden={product !== "classic"}>{pubText(product === "classic" && <ClassicPricing />)}</Box>
    </Stack>
  );
}
